import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { useAuth } from "../context/AuthContext";
import { Package, Users, FileText, DollarSign, MapPin, BarChart3, ChevronRight, Eye, CheckCircle, Clock, Trash2 } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [packages, setPackages] = useState([]);
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [tab]);

  const loadData = async () => {
    setLoading(true);
    try {
      if (tab === "overview" || !stats) {
        const s = await axios.get(`${API}/api/admin/stats`, { withCredentials: true });
        setStats(s.data);
      }
      if (tab === "submissions") {
        const s = await axios.get(`${API}/api/admin/submissions`, { withCredentials: true });
        setSubmissions(s.data);
      }
      if (tab === "packages") {
        const p = await axios.get(`${API}/api/admin/packages`, { withCredentials: true });
        setPackages(p.data);
      }
      if (tab === "payments") {
        const p = await axios.get(`${API}/api/admin/payments`, { withCredentials: true });
        setPayments(p.data);
      }
      if (tab === "users") {
        const u = await axios.get(`${API}/api/admin/users`, { withCredentials: true });
        setUsers(u.data);
      }
    } catch (e) { /* silent */ }
    setLoading(false);
  };

  const updateSubmissionStatus = async (id, status) => {
    await axios.put(`${API}/api/admin/submissions/${id}/status`, { status }, { withCredentials: true });
    loadData();
  };

  const toggleCircle = async (userId) => {
    await axios.post(`${API}/api/admin/users/${userId}/circle`, {}, { withCredentials: true });
    loadData();
  };

  const deletePackage = async (sku) => {
    if (!window.confirm(`Delete package ${sku}?`)) return;
    await axios.delete(`${API}/api/admin/packages/${sku}`, { withCredentials: true });
    loadData();
  };

  const TABS = [
    { key: "overview", label: "Overview", icon: BarChart3 },
    { key: "submissions", label: "Submissions", icon: FileText },
    { key: "packages", label: "Packages", icon: Package },
    { key: "payments", label: "Payments", icon: DollarSign },
    { key: "users", label: "Users", icon: Users },
  ];

  return (
    <>
      <Helmet><title>Admin Dashboard | Dispatch Hunts</title></Helmet>

      <section className="section-dark grain pt-28 pb-6" data-testid="admin-header">
        <div className="container-dh">
          <p className="text-label text-brass mb-2">Admin Panel</p>
          <h1 className="font-display text-2xl font-bold text-bone" data-testid="admin-title">Dashboard</h1>
        </div>
      </section>

      <section className="bg-carbon/95 border-b border-bone/10">
        <div className="container-dh flex gap-1 overflow-x-auto" data-testid="admin-tabs">
          {TABS.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setTab(key)}
              className={`flex items-center gap-2 px-5 py-3 font-label text-xs tracking-wider uppercase whitespace-nowrap transition-colors ${tab === key ? "bg-brass text-carbon" : "text-bone/60 hover:text-bone"}`}
              data-testid={`admin-tab-${key}`}>
              <Icon className="w-4 h-4" /> {label}
            </button>
          ))}
        </div>
      </section>

      <section className="section-light min-h-[60vh] py-10" data-testid="admin-content">
        <div className="container-dh">
          {loading && <p className="font-body text-slate">Loading...</p>}

          {/* Overview */}
          {tab === "overview" && stats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-stats">
              {[
                { label: "Packages", value: stats.packages, icon: Package },
                { label: "Species", value: stats.species, icon: MapPin },
                { label: "Users", value: stats.users, icon: Users },
                { label: "Submissions", value: stats.submissions, icon: FileText },
                { label: "New Submissions", value: stats.new_submissions, icon: Clock },
                { label: "Payments", value: stats.payments, icon: DollarSign },
                { label: "Revenue", value: `$${stats.total_revenue.toLocaleString()}`, icon: BarChart3 },
                { label: "Destinations", value: stats.destinations, icon: MapPin },
              ].map(({ label, value, icon: Icon }) => (
                <div key={label} className="bg-chalk border border-carbon/10 p-5" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center gap-3 mb-2"><Icon className="w-4 h-4 text-brass" /><span className="text-label text-[10px] tracking-wider uppercase text-smoke">{label}</span></div>
                  <p className="font-mono text-2xl font-bold text-carbon">{value}</p>
                </div>
              ))}
            </div>
          )}

          {/* Submissions */}
          {tab === "submissions" && (
            <div className="space-y-3" data-testid="admin-submissions">
              <p className="font-body text-sm text-slate mb-4">{submissions.length} total submissions</p>
              {submissions.map((sub) => (
                <div key={sub.id} className="bg-chalk border border-carbon/10 p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`submission-${sub.id}`}>
                  <div className="flex-1">
                    <p className="font-display text-base font-bold text-carbon">{sub.name}</p>
                    <p className="font-body text-sm text-slate">{sub.email} — {sub.phone}</p>
                    <p className="font-body text-xs text-smoke mt-1">{sub.adventure_type} | Budget: {sub.budget_range} | {sub.regions?.join(", ")}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {["new", "contacted", "booked", "closed"].map((s) => (
                      <button key={s} onClick={() => updateSubmissionStatus(sub.id, s)}
                        className={`px-3 py-1 text-label text-[10px] tracking-wider uppercase transition-colors ${sub.status === s ? "bg-brass text-carbon" : "bg-bone border border-carbon/10 text-smoke hover:text-carbon"}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Packages */}
          {tab === "packages" && (
            <div data-testid="admin-packages">
              <p className="font-body text-sm text-slate mb-4">{packages.length} packages</p>
              <div className="overflow-x-auto">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-carbon/15">
                      <th className="py-3 px-2 font-label text-[10px] tracking-wider uppercase text-smoke">SKU</th>
                      <th className="py-3 px-2 font-label text-[10px] tracking-wider uppercase text-smoke">Country</th>
                      <th className="py-3 px-2 font-label text-[10px] tracking-wider uppercase text-smoke">Type</th>
                      <th className="py-3 px-2 font-label text-[10px] tracking-wider uppercase text-smoke">Species</th>
                      <th className="py-3 px-2 font-label text-[10px] tracking-wider uppercase text-smoke">Price</th>
                      <th className="py-3 px-2 font-label text-[10px] tracking-wider uppercase text-smoke">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {packages.map((pkg) => (
                      <tr key={pkg.sku} className="border-b border-carbon/5 hover:bg-chalk/50" data-testid={`pkg-row-${pkg.sku}`}>
                        <td className="py-3 px-2 font-mono text-xs text-carbon">{pkg.sku}</td>
                        <td className="py-3 px-2 font-body text-sm text-carbon">{pkg.country}</td>
                        <td className="py-3 px-2 font-body text-xs text-slate">{pkg.subcategory}</td>
                        <td className="py-3 px-2 font-body text-xs text-slate max-w-[200px] truncate">{pkg.target_species?.substring(0, 40)}</td>
                        <td className="py-3 px-2 font-mono text-xs text-carbon">${pkg.price_low?.toLocaleString()}-${pkg.price_high?.toLocaleString()}</td>
                        <td className="py-3 px-2">
                          <button onClick={() => deletePackage(pkg.sku)} className="text-copper hover:text-copper/80"><Trash2 className="w-4 h-4" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Payments */}
          {tab === "payments" && (
            <div data-testid="admin-payments">
              <p className="font-body text-sm text-slate mb-4">{payments.length} payment transactions</p>
              {payments.length === 0 ? (
                <div className="bg-chalk border border-carbon/10 p-8 text-center">
                  <p className="font-body text-slate">No payments recorded yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payments.map((txn, i) => (
                    <div key={txn.session_id || i} className="bg-chalk border border-carbon/10 p-5 flex items-center gap-4">
                      <div className="flex-1">
                        <p className="font-display text-base font-bold text-carbon">{txn.expedition_name}</p>
                        <p className="font-body text-sm text-slate">{txn.user_email} — ${txn.amount}</p>
                        <p className="font-body text-xs text-smoke">{txn.created_at}</p>
                      </div>
                      <span className={`text-label text-[10px] tracking-wider uppercase px-2 py-1 ${txn.payment_status === "paid" ? "bg-field/20 text-field" : "bg-brass/20 text-brass"}`}>
                        {txn.payment_status}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Users */}
          {tab === "users" && (
            <div data-testid="admin-users">
              <p className="font-body text-sm text-slate mb-4">{users.length} registered users</p>
              <div className="space-y-3">
                {users.map((u) => (
                  <div key={u._id} className="bg-chalk border border-carbon/10 p-5 flex items-center gap-4" data-testid={`user-${u._id}`}>
                    <div className="flex-1">
                      <p className="font-display text-base font-bold text-carbon">{u.name}</p>
                      <p className="font-body text-sm text-slate">{u.email} — {u.role}</p>
                    </div>
                    <button onClick={() => toggleCircle(u._id)}
                      className={`px-3 py-1 text-label text-[10px] tracking-wider uppercase ${u.circle_member ? "bg-brass text-carbon" : "bg-bone border border-carbon/10 text-smoke hover:text-carbon"}`}
                      data-testid={`toggle-circle-${u._id}`}>
                      {u.circle_member ? "Circle Member" : "Not Circle"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
